import { twMerge } from 'tailwind-merge'
import useDragAndDrop from './useDragAndDrop'
import { useCallback, useMemo, useState } from 'react'
import { TFileUploadStatus } from './useS3FileUpload'
import FileUpload from './FileUpload'
import Typography from 'components/common/Typography'
import { faRemove } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface IProps {
  onFilesReady: CallbackWithParam<{ key: string; type: string; name: string }[]>
  discussionBoardId: string
}

const FileSizeLimit = 50

type TFileUpload = Array<TFileUploadStatus>

export default function FileUploads({
  onFilesReady,
  discussionBoardId
}: IProps) {
  const [fileStatuses, setFileStatuses] = useState<TFileUpload>([])
  const [files, setFiles] = useState<File[]>([])

  const upload = useCallback(
    async (fileList: FileList) => {
      const newFiles = Array.from(fileList).filter(
        file =>
          file.type === 'application/pdf' ||
          file.type.match('image.*') ||
          file.type.match('video.*')
      )

      setFiles([...files, ...newFiles])
    },
    [files]
  )

  const { dragActive, handleDrag, handleDrop } = useDragAndDrop(upload)

  const filesReady = useMemo(() => {
    return fileStatuses.filter(fileStatus => !!fileStatus.isFileReady)
  }, [fileStatuses])

  const filesError = useMemo(() => {
    return fileStatuses.filter(fileStatus => !!fileStatus.error)
  }, [fileStatuses])

  const onFileStatusChange = useCallback(
    (uploadStatus, i) => {
      const copy = [...fileStatuses]
      const status = copy[i]
      if (!status) {
        copy.push(uploadStatus)
      } else {
        copy.splice(i, 1, uploadStatus)
      }
      setFileStatuses(copy)

      const finishedUploadingAll =
        copy.filter(fileStatus => !!fileStatus.isFileReady).length ===
        copy.length

      if (finishedUploadingAll) {
        const filesData = copy
          .filter(status => !!status.s3Key && !status.userRemoved)
          .map(status => ({
            key: status.s3Key,
            type: status.type,
            name: status.name
          }))
        onFilesReady(filesData)
      }
    },
    [fileStatuses, onFilesReady]
  )

  const fileUploads = useMemo(
    () =>
      files.map((file, i) => {
        const status = fileStatuses[i]
        if (status && status.userRemoved) {
          return null
        }

        return (
          <div
            className="relative border border-gray-200 rounded-md"
            key={`file_${i}`}
          >
            <FileUpload
              discussionBoardId={discussionBoardId}
              file={file}
              sizeLimitMB={FileSizeLimit}
              onFileStatusChange={uploadStatus => {
                onFileStatusChange(uploadStatus, i)
              }}
            />

            {status?.isFileReady && (
              <FontAwesomeIcon
                icon={faRemove}
                className="absolute -top-2 -right-2 cursor-pointer bg-gray-400 shadow-sm rounded-full p-1 w-5 h-5 text-white"
                onClick={() => {
                  const statusCopy = { ...status }
                  statusCopy.userRemoved = true
                  onFileStatusChange(statusCopy, i)
                }}
              />
            )}
          </div>
        )
      }),
    [discussionBoardId, fileStatuses, files, onFileStatusChange]
  )

  return (
    <>
      <div
        className={twMerge(
          'py-4 rounded-lg border border-gray-200 flex flex-col w-full items-center gap-2',
          dragActive && 'bg-gray-100 shadow-sm'
        )}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <Typography
          color="gray"
          size="sm"
          className="flex items-center"
          component={'div'}
        >
          {`${filesReady.length} of ${fileStatuses.length} Uploaded`}
          {!!filesError.length && ` \u00b7 ${filesError.length} Errors`}
        </Typography>

        <div className="flex-1 flex flex-col items-center justify-center text-sm leading-6 text-gray-600">
          <label
            htmlFor="file-upload"
            className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
          >
            <span>Upload a file ({FileSizeLimit}MB max)</span>
            <input
              multiple
              id="file-upload"
              name="file-upload"
              type="file"
              className="sr-only"
              accept="image/*,video/*,application/pdf"
              onChange={e => {
                if (e.target.files?.length) {
                  upload(e.target.files)
                }
              }}
            />
          </label>
          <p className="pl-1">or drag and drop</p>
        </div>
      </div>

      {fileUploads}
    </>
  )
}
